import { useEffect, useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { Copy, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { MrDriveAvatar } from "@/components/MrDriveAvatar";
import { InstallGuide } from "@/components/InstallGuide";
import { DistrictHintBanner, districtHintHidden } from "@/components/DistrictHintBanner";
import { detectPlatform } from "@/lib/device";
import { enablePush } from "@/lib/push-client";
import { DEVICE_STEPS, shouldShowAfterDismiss, type NextStep } from "@/lib/next-step";
import { useNextStep } from "@/hooks/use-next-step";
import { useResidentRoles } from "@/lib/roles";

type DeviceStep = "safari" | "install" | "push";

const ls = {
  get(k: string) {
    try {
      return localStorage.getItem(k);
    } catch {
      return null;
    }
  },
  set(k: string, v: string) {
    try {
      localStorage.setItem(k, v);
    } catch {
      /* ignore */
    }
  },
  del(k: string) {
    try {
      localStorage.removeItem(k);
    } catch {
      /* ignore */
    }
  },
};

export const postWizardKey = (uid: string) => `td_post_wizard_modal_${uid}`;
const dismissKey = (uid: string, step: string) => `td_next_step_dismissed_${uid}_${step}`;
const standalonePushKey = (uid: string) => `td_standalone_push_prompt_${uid}`;
const EXPERT_DISMISS_KEY = "td_expert_card_dismissed";

async function copyUrl() {
  const url = window.location.href;
  try {
    await navigator.clipboard.writeText(url);
  } catch {
    const t = document.createElement("textarea");
    t.value = url;
    t.style.position = "fixed";
    t.style.opacity = "0";
    document.body.appendChild(t);
    t.select();
    try {
      document.execCommand("copy");
    } catch {
      /* ignore */
    }
    t.remove();
  }
  toast.success("Ссылка скопирована");
}

/** Карточка «Лучше открыть в Safari/Chrome» — на /auth и в окне «Последний шаг». */
export function OpenInBrowserCard({ onContinue }: { onContinue?: () => void }) {
  const [android, setAndroid] = useState(false);
  useEffect(() => setAndroid(detectPlatform() === "android"), []);
  const browser = android ? "Chrome" : "Safari";
  return (
    <div className="flex flex-col items-center text-center">
      <MrDriveAvatar variant="home" size={112} className="mb-4" />
      <h2 className="font-display text-2xl font-bold text-td-dark">Лучше открыть в {browser}</h2>
      <p className="mt-2 text-sm text-td-muted">
        Так приложение сохранится на экран телефона, и тебе не придётся входить заново
      </p>
      <ol className="mt-5 w-full space-y-3 rounded-2xl bg-td-soft/60 p-4 text-left text-lg font-semibold text-td-dark">
        {(android
          ? ["Нажми ⋮ вверху", "«Открыть в Chrome»"]
          : ["Нажми ··· вверху", "«Открыть во внешнем браузере»"]
        ).map((t, i) => (
          <li key={t} className="flex items-center gap-3">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-td-teal text-base font-bold text-white">
              {i + 1}
            </span>
            <span>{t}</span>
          </li>
        ))}
      </ol>
      <button
        type="button"
        onClick={() => void copyUrl()}
        className="mt-3 inline-flex min-h-11 items-center gap-1.5 text-sm text-td-teal underline underline-offset-4"
      >
        <Copy className="size-3.5" /> Не нашли кнопку? Скопировать ссылку и вставить в {browser}
      </button>
      {onContinue && (
        <button
          type="button"
          onClick={onContinue}
          className="mt-5 min-h-11 text-sm text-td-muted underline underline-offset-4"
        >
          Продолжить здесь
        </button>
      )}
    </div>
  );
}

function PushBody({ uid, onDone }: { uid: string; onDone: (granted: boolean) => void }) {
  const [busy, setBusy] = useState(false);
  const [denied, setDenied] = useState(false);
  const turnOn = async () => {
    setBusy(true);
    const r = await enablePush(uid);
    setBusy(false);
    if (r === "granted") {
      toast.success("Уведомления включены");
      onDone(true);
    } else if (r === "denied") setDenied(true);
    else if (r === "unsupported") toast("На этом устройстве уведомления не поддерживаются");
    else toast.error("Не получилось включить уведомления");
  };
  return (
    <div className="flex flex-col items-center text-center">
      <MrDriveAvatar variant="home" size={112} className="mb-4" />
      <h2 className="font-display text-2xl font-bold text-td-dark">Включи уведомления</h2>
      <p className="mt-2 text-sm text-td-muted">
        Чтобы не пропустить приглашения на кофе, встречи и ответы
      </p>
      {denied ? (
        <p className="mt-4 w-full rounded-xl bg-td-soft/60 p-3 text-sm text-td-dark">
          Уведомления сейчас запрещены. Включить их можно в настройках телефона: Настройки →
          TurkeyDrive → Уведомления
        </p>
      ) : (
        <button
          type="button"
          onClick={turnOn}
          disabled={busy}
          className="bg-td-teal text-white mt-5 flex min-h-12 w-full items-center justify-center rounded-full px-4 py-3 text-base font-semibold disabled:opacity-60"
        >
          {busy ? "Включаю…" : "Включить уведомления"}
        </button>
      )}
    </div>
  );
}

/** Окно с содержимым шага по центру экрана. */
export function NextStepDialog({
  step,
  light,
  uid,
  open,
  onClose,
  title,
}: {
  step: DeviceStep;
  light: boolean;
  uid: string;
  open: boolean;
  onClose: (granted?: boolean) => void;
  title?: string;
}) {
  const [platform, setPlatform] = useState<"ios" | "android" | "desktop">("desktop");
  useEffect(() => setPlatform(detectPlatform()), []);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90dvh] max-w-[calc(100vw-2rem)] overflow-y-auto rounded-2xl sm:max-w-md">
        <DialogTitle
          className={
            title
              ? "text-center text-xs font-semibold uppercase tracking-wide text-td-teal"
              : "sr-only"
          }
        >
          {title ?? "Следующий шаг"}
        </DialogTitle>
        <DialogDescription className="sr-only">
          Как настроить TurkeyDrive на телефоне
        </DialogDescription>
        {step === "safari" && <OpenInBrowserCard />}
        {step === "push" && <PushBody uid={uid} onDone={(g) => onClose(g)} />}
        {step === "install" &&
          (light ? (
            <div className="flex flex-col items-center text-center">
              <MrDriveAvatar variant="home" size={112} className="mb-4" />
              <h2 className="font-display text-2xl font-bold text-td-dark">Открой приложение</h2>
              <p className="mt-2 text-sm text-td-muted">
                Открой TurkeyDrive с экрана «Домой» и включи уведомления там
              </p>
            </div>
          ) : (
            <InstallGuide platform={platform} onInstalled={() => onClose()} />
          ))}
        <button
          type="button"
          onClick={() => onClose()}
          className="mx-auto mt-2 min-h-11 text-sm text-td-muted underline underline-offset-4"
        >
          {step === "push" ? "Не сейчас" : "Позже"}
        </button>
      </DialogContent>
    </Dialog>
  );
}

const CARD_TEXT: Record<Exclude<NextStep, null>, { title: string; hint: string; cta: string }> = {
  safari: {
    title: "Открой в Safari, чтобы установить приложение",
    hint: "Здесь, во встроенном браузере, это не работает",
    cta: "Как",
  },
  install: {
    title: "Установи приложение",
    hint: "Так придёт ответ на приглашение на кофе",
    cta: "Как",
  },
  push: {
    title: "Включи уведомления",
    hint: "Чтобы не пропустить встречи и ответы",
    cta: "Включить",
  },
  profile: { title: "Дозаполни профиль", hint: "", cta: "Заполнить" },
};

function NextStepCard({
  step,
  light,
  uid,
  missing,
  onDismiss,
  onRefresh,
}: {
  step: Exclude<NextStep, null>;
  light: boolean;
  uid: string;
  missing: number;
  onDismiss: () => void;
  onRefresh: () => void;
}) {
  const [open, setOpen] = useState(false);
  const t = CARD_TEXT[step];
  const title =
    step === "safari" && detectPlatform() === "android"
      ? "Открой в Chrome, чтобы установить приложение"
      : step === "install" && light
        ? "Открой приложение с экрана «Домой»"
        : t.title;
  const hint =
    step === "profile"
      ? `Осталось ${missing} — и я подберу тебе своих людей`
      : step === "install" && light
        ? "И включи уведомления там"
        : t.hint;
  const btn =
    "inline-flex min-h-9 shrink-0 items-center rounded-full bg-td-teal px-3 text-xs font-semibold text-white";
  return (
    <div className="relative flex items-center gap-3 rounded-2xl border border-td-teal/30 bg-td-teal/5 p-3 pr-10">
      <MrDriveAvatar variant="home" size={40} badgeSize={0} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-td-dark">{title}</p>
        {hint && <p className="truncate text-xs text-td-muted">{hint}</p>}
      </div>
      {step === "profile" ? (
        <Link to="/profile/setup" className={btn}>
          {t.cta}
        </Link>
      ) : !(step === "install" && light) ? (
        <button type="button" onClick={() => setOpen(true)} className={btn}>
          {t.cta}
        </button>
      ) : null}
      <button
        type="button"
        aria-label="Закрыть"
        onClick={onDismiss}
        className="absolute right-0 top-0 flex size-10 items-center justify-center rounded-full text-td-muted"
      >
        <X className="size-4" />
      </button>
      {step !== "profile" && (
        <NextStepDialog
          step={step}
          light={light}
          uid={uid}
          open={open}
          onClose={() => {
            setOpen(false);
            onRefresh();
          }}
        />
      )}
    </div>
  );
}

function ExpertCard({ onDismiss }: { onDismiss: () => void }) {
  return (
    <section className="relative rounded-2xl border border-td-line bg-td-soft/40 p-4 pr-12">
      <p className="text-sm font-semibold text-td-dark">💼 Ты эксперт или у тебя свой бизнес?</p>
      <p className="mt-1 text-sm text-td-muted">
        Расскажи о своих услугах — получишь роль «Эксперт».
      </p>
      <Link
        to="/profile"
        search={{ section: "services" }}
        className="mt-3 inline-flex items-center rounded-full border border-td-teal/40 bg-white px-4 py-2 text-sm font-semibold text-td-teal"
      >
        Рассказать об услугах
      </Link>
      <button
        type="button"
        aria-label="Скрыть"
        onClick={onDismiss}
        className="absolute right-1 top-1 flex size-11 items-center justify-center rounded-full text-td-muted hover:bg-muted"
      >
        <X className="size-4" />
      </button>
    </section>
  );
}

/**
 * Не более одного «ростового» напоминания: устройство → профиль → район → эксперт.
 */
export function GrowthReminder({
  withExpert = false,
  className = "",
}: {
  withExpert?: boolean;
  className?: string;
}) {
  const s = useNextStep();
  const { data: roles = [] } = useResidentRoles(s.uid);
  const [, bump] = useState(0);
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => setHydrated(true), []);
  if (!hydrated || !s.ready || !s.uid || !s.profile) return null;

  const uid = s.uid;
  if (s.step) {
    const at = Number(ls.get(dismissKey(uid, s.step)) || 0);
    if (shouldShowAfterDismiss(at, Date.now())) {
      return (
        <div className={className}>
          <NextStepCard
            step={s.step}
            light={s.light}
            uid={uid}
            missing={s.missing}
            onDismiss={() => {
              ls.set(dismissKey(uid, s.step!), String(Date.now()));
              bump((x) => x + 1);
            }}
            onRefresh={() => void s.refresh()}
          />
        </div>
      );
    }
  }

  const city = s.profile.city?.trim();
  if (city && city !== "Не указан" && !s.profile.district?.trim() && !districtHintHidden()) {
    return <DistrictHintBanner className={className} />;
  }

  const isExpert = roles.some((r) => r.role_key === "expert" && r.is_active);
  if (withExpert && !isExpert && ls.get(EXPERT_DISMISS_KEY) !== "1") {
    return (
      <div className={className}>
        <ExpertCard
          onDismiss={() => {
            ls.set(EXPERT_DISMISS_KEY, "1");
            bump((x) => x + 1);
          }}
        />
      </div>
    );
  }
  return null;
}

/**
 * Окна по центру: «Последний шаг» один раз после мастера и «Включи уведомления»
 * один раз при первом открытии установленного приложения.
 */
export function NextStepDialogs() {
  const s = useNextStep();
  const pathname = useRouterState({ select: (st) => st.location.pathname });
  const [shown, setShown] = useState<{ kind: "wizard" | "standalone"; step: DeviceStep } | null>(
    null,
  );

  useEffect(() => {
    if (!s.ready || !s.uid || shown || pathname.startsWith("/profile/setup")) return;
    const uid = s.uid;
    const device = DEVICE_STEPS.includes(s.step ?? null) ? (s.step as DeviceStep) : null;
    if (ls.get(postWizardKey(uid))) {
      ls.del(postWizardKey(uid));
      if (device) {
        if (s.standalone && device === "push") ls.set(standalonePushKey(uid), "1");
        setShown({ kind: "wizard", step: device });
        return;
      }
    }
    if (s.standalone && device === "push" && !ls.get(standalonePushKey(uid))) {
      ls.set(standalonePushKey(uid), "1");
      setShown({ kind: "standalone", step: "push" });
    }
  }, [s.ready, s.uid, s.step, s.standalone, pathname, shown]);

  if (!shown || !s.uid) return null;
  return (
    <NextStepDialog
      step={shown.step}
      light={s.light}
      uid={s.uid}
      open
      title={shown.kind === "wizard" ? "Последний шаг" : undefined}
      onClose={() => {
        setShown(null);
        void s.refresh();
      }}
    />
  );
}
